import React, { useEffect, useMemo, useState } from "react";
import BaseChart from "../../../Components/Analytics/BaseChart";
import { useAuth } from "../../../Context/AuthContext";
import { useChartTheme } from "../../../Context/ThemeContext";
import { getOrganizationAnalytics } from "../../../Services/OrganizationService";
import {
    appointmentFunnelOption,
    appointmentStatusOption,
    ageDistributionOption,
    ageHeatmapOption,
    cityDistributionOption,
    completionGaugeOption,
    facilityPatientsOption,
    facilityPerformanceRadarOption,
    facilityTreemapOption,
    financialScatterOption,
    patientsComparisonOption,
    providerPerformanceOption,
    sankeyOption,
    stateDistributionOption,
    sunburstOption,
} from "../../../Utils/AnalyticsOptions";
import {
    getTopKPIs,
    transformAgeDistribution,
    transformAgeHeatmap,
    transformAppointmentFunnel,
    transformAppointmentStatus,
    transformCityDistribution,
    transformCompletionGauge,
    transformFacilityPatients,
    transformFacilityPerformanceRadar,
    transformFacilityTreemap,
    transformFinancialScatter,
    transformPatientsAppointmentsVisits,
    transformProviderPerformance,
    transformSankey,
    transformStateDistribution,
    transformSunburst,
} from "../../../Utils/AnalyticsTransformers";

const resolveOrganizationId = (decodedToken) => {
    const possibleValues = [
        decodedToken?.organization_id,
        decodedToken?.OrganizationId,
        decodedToken?.organizationId,
        decodedToken?.org_id,
    ];

    return possibleValues.find((value) => value !== undefined && value !== null && value !== "") || null;
};

const normalizeAnalyticsPayload = (payload) => {
    if (Array.isArray(payload)) return payload;
    if (Array.isArray(payload?.data)) return payload.data;
    if (Array.isArray(payload?.Data)) return payload.Data;
    if (Array.isArray(payload?.analytics)) return payload.analytics;
    if (Array.isArray(payload?.result)) return payload.result;
    return [];
};

export default function OrganizationAnalyticsView({ analyticsData, loading: externalLoading, error: externalError }) {
  useChartTheme();
    const { token, decodedToken } = useAuth();
    const [analytics, setAnalytics] = useState([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);

    useEffect(() => {
        // If parent provided analytics data, use it and skip the internal fetch
        if (analyticsData !== undefined) {
            setAnalytics(normalizeAnalyticsPayload(analyticsData));
            setLoading(Boolean(externalLoading));
            setError(externalError || null);
            return;
        }

        const organizationId = resolveOrganizationId(decodedToken) || localStorage.getItem("organizationId");

        const loadAnalytics = async () => {
            if (!token || !organizationId) {
                setAnalytics([]);
                setLoading(false);
                setError(null);
                return;
            }

            setLoading(true);
            setError(null);

            try {
                const response = await getOrganizationAnalytics(organizationId, token);
                setAnalytics(normalizeAnalyticsPayload(response));
            } catch (err) {
                setError(err);
            } finally {
                setLoading(false);
            }
        };

        loadAnalytics();
    }, [decodedToken, token, analyticsData, externalLoading, externalError]);

    // allow external or parent-managed data via `productivity` variable
    const sourceAnalytics = useMemo(() => normalizeAnalyticsPayload(analytics), [analytics]);
    const kpis = useMemo(() => getTopKPIs(sourceAnalytics), [sourceAnalytics]);
    const providerPerformanceData = useMemo(() => transformProviderPerformance(sourceAnalytics), [sourceAnalytics]);
    const facilityPatientsData = useMemo(() => transformFacilityPatients(sourceAnalytics), [sourceAnalytics]);
    const appointmentStatusData = useMemo(() => transformAppointmentStatus(sourceAnalytics), [sourceAnalytics]);
    const facilityPerformanceRadarData = useMemo(() => transformFacilityPerformanceRadar(sourceAnalytics), [sourceAnalytics]);
    const ageDistributionData = useMemo(() => transformAgeDistribution(sourceAnalytics), [sourceAnalytics]);
    const stateDistributionData = useMemo(() => transformStateDistribution(sourceAnalytics), [sourceAnalytics]);
    const cityDistributionData = useMemo(() => transformCityDistribution(sourceAnalytics), [sourceAnalytics]);
    const patientsComparisonData = useMemo(() => transformPatientsAppointmentsVisits(sourceAnalytics), [sourceAnalytics]);
    const facilityTreemapData = useMemo(() => transformFacilityTreemap(sourceAnalytics), [sourceAnalytics]);
    const heatmapData = useMemo(() => transformAgeHeatmap(sourceAnalytics), [sourceAnalytics]);
    const financialScatterData = useMemo(() => transformFinancialScatter(sourceAnalytics), [sourceAnalytics]);
    const completionGaugeData = useMemo(() => transformCompletionGauge(sourceAnalytics), [sourceAnalytics]);
    const appointmentFunnelData = useMemo(() => transformAppointmentFunnel(sourceAnalytics), [sourceAnalytics]);
    const sunburstData = useMemo(() => transformSunburst(sourceAnalytics), [sourceAnalytics]);
    const sankeyData = useMemo(() => transformSankey(sourceAnalytics), [sourceAnalytics]);
    const hasData = sourceAnalytics.length > 0;

    const metricCards = [
        { label: "Patients", value: kpis.patients.toLocaleString(), accent: "primary" },
        { label: "Appointments", value: kpis.appointments.toLocaleString(), accent: "info" },
        { label: "Visits", value: kpis.visits.toLocaleString(), accent: "success" },
        { label: "Facilities", value: kpis.facilities.toLocaleString(), accent: "warning" },
    ];

    return (
        <div className="hc-page-bg min-vh-100 p-3 p-lg-4">
            <div className="d-flex flex-column flex-lg-row justify-content-between align-items-start gap-2 mb-4">
                <div>
                    <h3 className="fw-bold mb-1">Productivity Analytics</h3>
                    <p className="text-secondary mb-0">
                        Review care delivery, provider utilization, and appointment flow across your organization.
                    </p>
                </div>
                <div className="text-muted small">{hasData ? `${analytics.length} facilities included` : "Waiting for analytics"}</div>
            </div>

            <div className="row g-3 mb-4">
                {metricCards.map((card) => (
                    <div key={card.label} className="col-12 col-md-6 col-xl-3">
                        <div className={`hc-surface rounded-4 p-3 h-100 ${card.accent === "primary" ? "hc-card-primary" : ""}`}>
                            <div className="text-secondary small text-uppercase fw-semibold">{card.label}</div>
                            <div className="display-6 fw-bold mt-2">{card.value}</div>
                        </div>
                    </div>
                ))}
            </div>

            <div className="row g-3">
                <div className="col-12 col-xl-6">
                    <BaseChart
                        title="Provider performance"
                        subtitle="Revenue and volume by provider"
                        loading={loading}
                        noData={!loading && !hasData}
                        error={error}
                        option={providerPerformanceOption({ data: providerPerformanceData })}
                    />
                </div>
                <div className="col-12 col-xl-6">
                    <BaseChart
                        title="Facility patient volume"
                        subtitle="Patient counts by facility"
                        loading={loading}
                        noData={!loading && !hasData}
                        error={error}
                        option={facilityPatientsOption({ data: facilityPatientsData })}
                    />
                </div>
                <div className="col-12 col-xl-6">
                    <BaseChart
                        title="Appointment status"
                        subtitle="Distribution across the care journey"
                        loading={loading}
                        noData={!loading && !hasData}
                        error={error}
                        option={appointmentStatusOption({ data: appointmentStatusData })}
                    />
                </div>
                <div className="col-12 col-xl-6">
                    <BaseChart
                        title="Facility performance radar"
                        subtitle="Compare utilization across facilities"
                        loading={loading}
                        noData={!loading && !hasData}
                        error={error}
                        option={facilityPerformanceRadarOption({ data: facilityPerformanceRadarData })}

                    />
                </div>
                <div className="col-12 col-xl-6">
                    <BaseChart
                        title="Appointment age distribution"
                        subtitle="Age groups for scheduled visits"
                        loading={loading}
                        noData={!loading && !hasData}
                        error={error}
                        option={ageDistributionOption({ data: ageDistributionData })}

                    />
                </div>
                <div className="col-12 col-xl-6">
                    <BaseChart
                        title="Completion gauge"
                        subtitle="Seen appointments against total"
                        loading={loading}
                        noData={!loading && !hasData}
                        error={error}
                        option={completionGaugeOption({ value: completionGaugeData.value })}

                    />
                </div>
                <div className="col-12 col-xl-6">
                    <BaseChart
                        title="Appointment funnel"
                        subtitle="Journey from scheduling to seen"
                        loading={loading}
                        noData={!loading && !hasData}
                        error={error}
                        option={appointmentFunnelOption({ data: appointmentFunnelData })}

                    />
                </div>
                <div className="col-12 col-xl-6">
                    <BaseChart
                        title="Financial scatter"
                        subtitle="Insurance pay versus patient payment"
                        loading={loading}
                        noData={!loading && !hasData}
                        error={error}
                        option={financialScatterOption({ data: financialScatterData })}

                    />
                </div>
                <div className="col-12 col-xl-6">
                    <BaseChart
                        title="State distribution"
                        subtitle="Appointment concentration by state"
                        loading={loading}
                        noData={!loading && !hasData}
                        error={error}
                        option={stateDistributionOption({ data: stateDistributionData })}

                    />
                </div>
                <div className="col-12 col-xl-6">
                    <BaseChart
                        title="Top patient cities"
                        subtitle="Most common appointment cities"
                        loading={loading}
                        noData={!loading && !hasData}
                        error={error}
                        option={cityDistributionOption({ data: cityDistributionData })}

                    />
                </div>
                <div className="col-12 col-xl-6">
                    <BaseChart
                        title="Patient vs appointments"
                        subtitle="Compare patient count and appointment count"
                        loading={loading}
                        noData={!loading && !hasData}
                        error={error}
                        option={patientsComparisonOption({ data: patientsComparisonData, comparison: "Appointments", name: "Patient comparisons" })}

                    />
                </div>
                <div className="col-12 col-xl-6">
                    <BaseChart
                        title="Facility appointment treemap"
                        subtitle="Appointment intensity by facility"
                        loading={loading}
                        noData={!loading && !hasData}
                        error={error}
                        option={facilityTreemapOption({ data: facilityTreemapData })}

                    />
                </div>
                <div className="col-12 col-xl-6">
                    <BaseChart
                        title="Facility vs age heatmap"
                        subtitle="Appointment age mix by facility"
                        loading={loading}
                        noData={!loading && !hasData}
                        error={error}
                        option={ageHeatmapOption({ facilityNames: heatmapData.facilityNames, ageKeys: heatmapData.ageKeys, rows: heatmapData.rows })}
                        height="600px"
                    />
                </div>
                <div className="col-12 col-xl-6">
                    <BaseChart
                        title="Provider relationship flow"
                        subtitle="Organization to facility to provider"
                        loading={loading}
                        noData={!loading && !hasData}
                        error={error}
                        option={sankeyOption({ nodes: sankeyData.nodes, links: sankeyData.links })}
                        height="600px"
                    />
                </div>

            </div>
        </div>
    );
}
